import { z } from 'zod';
import { roleSchema } from './permissions.js';
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .pipe(z.email('Enter a valid email address'));
/**
 * An email that may be left blank. Forms submit '' for an empty field, which must mean "none" (null) rather than
 * an invalid address; `undefined` stays `undefined` so PATCH requests do not wipe it.
 */
export const optionalEmailSchema = z
    .string()
    .trim()
    .max(254)
    .nullish()
    .superRefine((v, ctx) => {
    if (v && !z.email().safeParse(v).success)
        ctx.addIssue({ code: 'custom', message: 'Enter a valid email address' });
})
    .transform((v) => (v === undefined ? undefined : v === null || v === '' ? null : v.toLowerCase()))
    .optional();
export const PASSWORD_MIN = 12;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters`)
    .max(128, 'Use at most 128 characters');
export const registerSchema = z.object({
    fullName: z.string().trim().min(2, 'Enter your full name').max(120),
    email: emailSchema,
    password: passwordSchema,
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Enter your password').max(128),
});
export const verifyEmailSchema = z.object({ token: z.string().min(20).max(200) });
export const resendVerificationSchema = z.object({ email: emailSchema });
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({
    token: z.string().min(20).max(200),
    password: passwordSchema,
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: passwordSchema,
});
export const mfaCodeSchema = z.object({
    // Either a 6-digit authenticator code or a recovery code (xxxx-xxxx-xxxx).
    code: z
        .string()
        .trim()
        .min(6)
        .max(24)
        .regex(/^[A-Za-z0-9-]+$/, 'Enter the code exactly as shown'),
});
export const mfaDisableSchema = z.object({
    password: z.string().min(1).max(128),
    code: mfaCodeSchema.shape.code,
});
export const acceptInvitationSchema = z.object({
    token: z.string().min(20).max(200),
    fullName: z.string().trim().min(2).max(120).optional(),
    password: passwordSchema.optional(),
});
// ───────────── responses ─────────────
export const membershipSummarySchema = z.object({
    businessId: z.uuid(),
    businessName: z.string(),
    role: roleSchema,
    status: z.enum(['invited', 'active', 'revoked']),
});
export const userSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    fullName: z.string(),
    phone: z.string().nullable(),
    emailVerified: z.boolean(),
    mfaEnabled: z.boolean(),
    lastLoginAt: z.string().nullable(),
});
export const sessionResponseSchema = z.object({
    user: userSchema,
    memberships: z.array(membershipSummarySchema),
    pendingInvitations: z.array(membershipSummarySchema),
    csrfToken: z.string(),
    mfaVerified: z.boolean(),
});
export const loginResponseSchema = z.object({
    status: z.enum(['ok', 'mfa_required']),
});
export const mfaEnrollmentSchema = z.object({
    otpauthUri: z.string(),
    secret: z.string(),
    qrDataUrl: z.string(),
});
export const mfaConfirmedSchema = z.object({ recoveryCodes: z.array(z.string()) });
export const sessionInfoSchema = z.object({
    id: z.uuid(),
    createdAt: z.string(),
    lastSeenAt: z.string(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    current: z.boolean(),
});
//# sourceMappingURL=auth.js.map